import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../../services/api";

// Thunks
export const fetchReservations = createAsyncThunk(
  "reservations/fetchReservations",
  async ({ page = 1, pageSize = 10 } = {}, { rejectWithValue }) => {
    try {
      const r = await api.get('/Reservation', { params: { page, pageSize } });
      return r.data; // PagedResult<ReservationResultDto>
    } catch (error) {
      return rejectWithValue(error.message || "Erreur de connexion au serveur");
    }
  }
);

export const createReservation = createAsyncThunk(
  "reservations/createReservation",
  async (reservationData, { rejectWithValue }) => {
    try {
      const formatDateToISO = (dateStr) => {
        if (!dateStr) return null;
        try { return new Date(dateStr).toISOString(); } catch { return dateStr; }
      };
      const payload = {
        clientId: reservationData.clientId,
        vehiculeId: reservationData.vehiculeId,
        lieuDepart: parseInt(reservationData.lieuDepart) || 0,
        lieuRetour: parseInt(reservationData.lieuRetour) || 0,
        statut: parseInt(reservationData.statut) || 0,
        dateDebut: formatDateToISO(reservationData.dateDebut),
        dateFin: formatDateToISO(reservationData.dateFin),
        prixParJourOverride: reservationData.prixParJourOverride ?? null,
      };
      const response = await api.post("/Reservation", payload);
      return response.data; // newId (Guid) uniquement
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message || "Erreur lors de la création de la réservation");
    }
  }
);

export const updateReservation = createAsyncThunk(
  "reservations/updateReservation",
  async ({ id, reservationData }, { rejectWithValue }) => {
    try {
      const formatDateToISO = (dateStr) => {
        if (!dateStr) return null;
        try { return new Date(dateStr).toISOString(); } catch { return dateStr; }
      };
      const payload = {
        lieuDepart: parseInt(reservationData.lieuDepart) || 0,
        lieuRetour: parseInt(reservationData.lieuRetour) || 0,
        statut: parseInt(reservationData.statut) || 0,
        dateDebut: formatDateToISO(reservationData.dateDebut),
        dateFin: formatDateToISO(reservationData.dateFin),
      };
      const response = await api.patch(`/Reservation/${id}`, payload);
      return response.data; // ReservationResultDto complet (backend retourne Ok(updated))
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message || "Erreur lors de la modification de la réservation");
    }
  }
);

export const deleteReservation = createAsyncThunk(
  "reservations/deleteReservation",
  async (id, { rejectWithValue }) => {
    try {
      await api.delete(`/Reservation/${id}`);
      return id;
    } catch (error) {
      return rejectWithValue(error.message || "Erreur lors de la suppression de la réservation");
    }
  }
);

export const getReservationById = createAsyncThunk(
  "reservations/getReservationById",
  async (id, { rejectWithValue }) => {
    try {
      const response = await api.get(`/Reservation/${id}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message || "Erreur lors de la récupération de la réservation");
    }
  }
);

export const getReservationsByStatut = createAsyncThunk(
  "reservations/getReservationsByStatut",
  async (statut, { rejectWithValue }) => {
    try {
      const response = await api.get(`/Reservation/statut/${statut}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message || "Erreur lors de la récupération des réservations");
    }
  }
);

export const searchReservations = createAsyncThunk(
  "reservations/searchReservations",
  async ({ nomClient, date }, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams();
      if (nomClient) params.append("nomClient", nomClient);
      if (date) params.append("date", date);
      const response = await api.get(`/Reservation/search?${params.toString()}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message || "Erreur lors de la recherche de réservations");
    }
  }
);

const initialState = {
  items: [],
  totalCount: 0,
  totalPages: 1,
  currentPage: 1,
  pageSize: 10,
  loading: false,
  error: null,
  fetchError: null,
  success: false,
};

const reservationsSlice = createSlice({
  name: "reservations",
  initialState,
  reducers: {
    resetSuccess: (state) => { state.success = false; },
    clearError: (state) => { state.error = null; state.fetchError = null; },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchReservations.pending, (state) => { state.loading = true; state.fetchError = null; state.error = null; })
      .addCase(fetchReservations.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload.items;
        state.totalCount = action.payload.totalCount;
        state.totalPages = action.payload.totalPages;
        state.currentPage = action.payload.page;
        state.fetchError = null;
      })
      .addCase(fetchReservations.rejected, (state, action) => { state.loading = false; state.fetchError = action.payload; });

    builder
      .addCase(createReservation.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(createReservation.fulfilled, (state) => { state.loading = false; state.success = true; })
      .addCase(createReservation.rejected, (state, action) => { state.loading = false; state.error = action.payload; });

    builder
      .addCase(updateReservation.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(updateReservation.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload?.id) {
          const index = state.items.findIndex(r => r.id === action.payload.id);
          if (index !== -1) state.items[index] = action.payload;
        }
        state.success = true;
      })
      .addCase(updateReservation.rejected, (state, action) => { state.loading = false; state.error = action.payload; });

    builder
      .addCase(deleteReservation.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(deleteReservation.fulfilled, (state, action) => {
        state.loading = false;
        state.items = state.items.filter(r => r.id !== action.payload);
        state.totalCount = Math.max(0, state.totalCount - 1);
        state.success = true;
      })
      .addCase(deleteReservation.rejected, (state, action) => { state.loading = false; state.error = action.payload; });

    builder
      .addCase(getReservationById.pending, (state) => { state.loading = true; state.fetchError = null; })
      .addCase(getReservationById.fulfilled, (state) => { state.loading = false; state.fetchError = null; })
      .addCase(getReservationById.rejected, (state, action) => { state.loading = false; state.fetchError = action.payload; });

    builder
      .addCase(getReservationsByStatut.pending, (state) => { state.loading = true; state.fetchError = null; })
      .addCase(getReservationsByStatut.fulfilled, (state, action) => { state.loading = false; state.items = action.payload; state.fetchError = null; })
      .addCase(getReservationsByStatut.rejected, (state, action) => { state.loading = false; state.fetchError = action.payload; });

    builder
      .addCase(searchReservations.pending, (state) => { state.loading = true; state.fetchError = null; })
      .addCase(searchReservations.fulfilled, (state, action) => { state.loading = false; state.items = action.payload; state.fetchError = null; })
      .addCase(searchReservations.rejected, (state, action) => { state.loading = false; state.fetchError = action.payload; });
  },
});

export const { resetSuccess, clearError } = reservationsSlice.actions;
export default reservationsSlice.reducer;
